// The building blocks of a list pane's navigation (13-ui-ux.md §1): a titled section with its
// count, rows with a tile, a name on up to two lines and a line of details, and the "see all"
// link at the end of a section. Shared by the projects and the shared spaces of the Projects app.
import { DirectionalIcon, brandTokens, layout } from '@batiflow/ui';
import { __css, mergeClasses, tokens } from '@fluentui/react-components';
import { ArrowRight16Regular } from '@fluentui/react-icons';
import { useId, type ReactNode } from 'react';

const usePaneNavStyles = __css({"section":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r"},"sectionHead":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","qttt6h":"f1e5rei4","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv"},"sectionTitle":{"jrapky":0,"Frg6f3":0,"t21cq0":0,"B6of3ja":0,"B74szlk":"f1s184ao","Bh6795r":"fqerorx","Be2twd7":"fy9rknc","Bg96gwp":"fwrc4pm","Bhrd7zp":"fl43uef","ublp8o":"f1fciab8","B9bfxx9":"f1u78bsq","sj55zd":"f11d4kpn"},"count":{"Bgto7am":"f1cd1ik7","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1nqfz0o","B7ck84d":"f1ewtqcl","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f44lkw9","De3pzq":"f1ac3ntu","sj55zd":"fkfq4zb","Be2twd7":"f13mqy1h","Bg96gwp":"fwbovi1","Bhrd7zp":"fl43uef","fsow6f":"f17mccla"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"fbi42co"},"item":{"qhf8xq":"f10pi13n","mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1xcgk68","qttt6h":"ff3ufwa","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","B1hrpyx":"fmm5bso","Bnt446c":"fgcytg1","B7ck84d":"f1ewtqcl","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","sj55zd":"f19n0e5","Bi91k9c":"feu1g3u","Jwef8y":"f1t94bn6","Bde5pd6":"fbnuktb"},"active":{"sj55zd":"fulqd5u","De3pzq":"f1bgiulx","Bi91k9c":"f1p45l4d","Jwef8y":"f1rlav19","Ftih45":"f1wl9k8s","Brfgrao":"f1j7ml58","B2xlx2h":"f1d2slso","Boxfb1f":0,"Bj7puwp":0,"s8xqzv":"f68jq20","B2zvqh9":"fk1k89f","r59vdv":0,"Budzafs":0,"ck0cow":0,"n07z76":0,"Gng75u":"fielpny","cmx5o7":"f1jzd0na"},"tile":{"Bnnss6s":"fi64zpg","mc9l5x":"f22iagw"},"text":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Bgto7am":"fuzgk9b","Belr9w4":"fbi42co"},"name":{"mc9l5x":"f1rvi9lw","qk3uaq":"f1h500bm","Bqc2kz9":"fi1h9r9","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","Btd35i7":"fjksvth","Be2twd7":"fkhj508","Bg96gwp":"f1i3iumi"},"nameActive":{"Bhrd7zp":"fl43uef"},"meta":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"fbi42co","Be2twd7":"fy9rknc","Bg96gwp":"fwrc4pm","sj55zd":"f11d4kpn"},"metaActive":{"sj55zd":"fulqd5u"},"note":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv","Be2twd7":"fy9rknc","sj55zd":"fkfq4zb"},"footer":{"mc9l5x":"ftuwxu6","Bt984gj":"f122n59","qb2dma":"fjgzulp","i8kkvl":"f1ufnopg","qttt6h":"f12ffk1x","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv","Be2twd7":"fy9rknc","Bhrd7zp":"fl43uef"}});

export { usePaneNavStyles };

export interface PaneSectionProps {
  title: string;
  /** Shown next to the title once known. */
  count?: number;
  /** Accessible name of the count ("3 projects"). */
  countLabel?: string;
  children: ReactNode;
}

/** A titled group of the pane, with the number of rows it lists. */
export function PaneSection({ title, count, countLabel, children }: PaneSectionProps) {
  const styles = usePaneNavStyles();
  const id = useId();
  return (
    <section className={styles.section} aria-labelledby={id}>
      <div className={styles.sectionHead}>
        <h3 id={id} className={styles.sectionTitle}>
          {title}
        </h3>
        {count !== undefined ? (
          <span
            className={styles.count}
            dir="ltr"
            {...(countLabel ? { role: 'img', 'aria-label': countLabel } : {})}
          >
            {count}
          </span>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export interface PaneRowContentProps {
  tile: ReactNode;
  name: ReactNode;
  meta?: ReactNode;
  active?: boolean;
}

/** The inside of a row (for the caller's link): tile, name and the line of details. */
export function PaneRowContent({ tile, name, meta, active = false }: PaneRowContentProps) {
  const styles = usePaneNavStyles();
  return (
    <>
      <span className={styles.tile}>{tile}</span>
      <span className={styles.text}>
        <span className={mergeClasses(styles.name, active && styles.nameActive)}>{name}</span>
        {meta ? (
          <span className={mergeClasses(styles.meta, active && styles.metaActive)}>{meta}</span>
        ) : null}
      </span>
    </>
  );
}

/** The arrow of a "see all" link, mirrored in Arabic. */
export function PaneFooterArrow() {
  return <DirectionalIcon icon={ArrowRight16Regular} />;
}


import "/opt/batiflow/app/apps/web/src/shared/components/pane-nav.griffel.css";